// Phase 26A · Global search top-bar component.
// Keyboard shortcut: `/` focuses (unless a text input is already focused).
// Results grouped by type. Hidden entirely for SE / DISTRIBUTOR roles.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ALLOWED_ROLES = new Set([
  "SUPER_ADMIN",
  "MANAGEMENT",
  "MIS",
  "ACCOUNTS",
  "SALES_HEAD",
  "ZSM",
  "RSM",
  "ASM",
]);

const GROUP_ORDER = [
  ["screens", "Screens"],
  ["reports", "Reports"],
  ["outlets", "Outlets"],
  ["distributors", "Distributors"],
  ["beats", "Beats"],
  ["users", "Users"],
  ["orders", "Orders"],
];

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const allowed = user && ALLOWED_ROLES.has(user.role_code);

  // "/" shortcut to focus the search.
  useEffect(() => {
    if (!allowed) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      const isEditable = tag === "input" || tag === "textarea" || t?.isContentEditable;
      if (e.key === "/" && !isEditable) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowed]);

  // Click outside closes dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!allowed) return;
    if (!q || q.trim().length < 2) {
      setResults(null);
      setError("");
      return;
    }
    setBusy(true);
    setError("");
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/global-search", { params: { q: q.trim() } });
        setResults(data);
      } catch (e) {
        setError(formatApiError(e));
        setResults(null);
      } finally {
        setBusy(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [q, allowed]);

  const totalCount = useMemo(() => {
    if (!results) return 0;
    return GROUP_ORDER.reduce((n, [k]) => n + (results[k]?.length || 0), 0);
  }, [results]);

  const go = (route) => {
    setOpen(false);
    setQ("");
    setResults(null);
    if (route) navigate(route);
  };

  if (!allowed) return null;

  return (
    <div ref={boxRef} className="relative flex-1 max-w-md" data-testid="global-search">
      <div
        className={`flex items-center gap-2 h-9 px-3 rounded-full border transition-colors ${
          open
            ? "border-[#6D2C91] bg-white shadow-sm"
            : "border-neutral-200 bg-neutral-50 hover:bg-white"
        }`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
        ) : (
          <Search className="h-4 w-4 text-neutral-400" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search screens, outlets, users… (press /)"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-400"
          data-testid="global-search-input"
        />
        {q ? (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setResults(null);
              inputRef.current?.focus();
            }}
            className="text-neutral-400 hover:text-neutral-600"
            data-testid="global-search-clear"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="hidden sm:inline-flex h-5 px-1.5 items-center rounded border border-neutral-200 bg-white text-[10px] text-neutral-500 font-mono">
            /
          </kbd>
        )}
      </div>

      {open && (q.trim().length >= 2 || error) && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-xl border border-neutral-200 bg-white shadow-lg max-h-[70vh] overflow-y-auto z-50"
          data-testid="global-search-dropdown"
        >
          {error ? (
            <div className="p-4 text-sm text-red-600" data-testid="global-search-error">
              {error}
            </div>
          ) : busy && !results ? (
            <div className="p-4 flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : results && totalCount === 0 ? (
            <div
              className="p-4 text-sm text-neutral-500"
              data-testid="global-search-empty"
            >
              No results for “{results.q}”.
            </div>
          ) : (
            <div className="py-2">
              {GROUP_ORDER.map(([key, label]) => {
                const rows = results?.[key] || [];
                if (!rows.length) return null;
                return (
                  <div key={key} className="mb-1" data-testid={`global-search-group-${key}`}>
                    <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                      {label}
                    </div>
                    {rows.map((row, i) => (
                      <button
                        key={row.id || row.route || i}
                        type="button"
                        onClick={() => go(row.route)}
                        className="w-full text-left px-4 py-2 hover:bg-neutral-50 transition-colors flex items-center justify-between gap-3"
                        data-testid={`global-search-result-${key}-${i}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-neutral-800 truncate">{row.title}</div>
                          {row.subtitle ? (
                            <div className="text-xs text-neutral-500 truncate">
                              {row.subtitle}
                            </div>
                          ) : null}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-400 shrink-0">
                          {label.replace(/s$/, "")}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
